import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminDeleteConfirmation from "@/components/AdminDeleteConfirmation";

const empty = {
  name: "", date: "", time: "", location: "", city: "",
  category: "", modality: "Clássico", fee: "", registration_deadline: "",
  registration_link: "", info: "", featured: false, participant_ids: [], poster_url: null,
};

export default function AdminTournaments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [athleteError, setAthleteError] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(null);
  const load = () => api.get("/tournaments").then(r => setItems(r.data)).catch(() => toast.error("Não foi possível carregar os torneios."));
  useEffect(() => { load(); }, []);
  const loadAthletes = async () => {
    setLoadingAthletes(true); setAthleteError(false);
    try { const { data } = await api.get("/athletes"); setAthletes(data); }
    catch { setAthleteError(true); }
    finally { setLoadingAthletes(false); }
  };
  const begin = (tournament = null) => {
    setEditingId(tournament?.id || null);
    setForm(tournament ? Object.fromEntries(Object.entries(empty).map(([key, fallback]) => [key, tournament[key] ?? fallback])) : { ...empty });
    setQuery(""); setOpen(true); loadAthletes();
  };
  const save = async (e) => {
    e.preventDefault();
    if (saving || loadingAthletes || athleteError) return;
    setSaving(true);
    try {
      if (editingId) await api.put(`/tournaments/${editingId}`, form);
      else await api.post("/tournaments", form);
      toast.success(editingId ? "Torneio atualizado" : "Torneio criado");
      setForm(empty); setOpen(false); load();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/tournaments/${id}`); load(); }
    catch { toast.error("Não foi possível excluir o torneio."); }
  };
  const exportPdf = async (tournament) => {
    if (exporting) return;
    setExporting(tournament.id);
    try {
      const { data } = await api.get(`/tournaments/${tournament.id}/participants/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `participantes-${tournament.name.replace(/[<>:"/\\|?*\x00-\x1f]/g, "-")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      let detail = "Não foi possível gerar o PDF.";
      try { detail = JSON.parse(await error.response.data.text()).detail; } catch {}
      toast.error(formatApiError(detail));
    } finally { setExporting(null); }
  };
  const toggleAthlete = (id) => setForm(current => ({ ...current,
    participant_ids: current.participant_ids.includes(id)
      ? current.participant_ids.filter(value => value !== id) : [...current.participant_ids, id],
  }));
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const filteredAthletes = athletes.filter(athlete =>
    [athlete.name, athlete.category, athlete.school, athlete.email].some(value =>
      (value || "").toLocaleLowerCase("pt-BR").includes(normalizedQuery)));
  const missingParticipants = !loadingAthletes && !athleteError
    ? form.participant_ids.filter(id => !athletes.some(athlete => athlete.id === id)) : [];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <AppLayout kind="admin">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl text-white font-semibold">Gestão de Torneios</h1>
        <Dialog open={open} onOpenChange={value => { if (!saving) setOpen(value); }}>
          <DialogTrigger asChild>
            <Button onClick={() => begin()} data-testid="admin-add-tournament-btn" className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">+ Novo Torneio</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#071A33] border-amber-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Editar Torneio" : "Novo Torneio"}</DialogTitle></DialogHeader>
            <form onSubmit={save}>
              <fieldset disabled={saving} className="grid sm:grid-cols-2 gap-3">
              {[
                ["name","Nome",true], ["date","Data (YYYY-MM-DD)",true], ["time","Horário"],
                ["location","Local"], ["city","Cidade"], ["category","Categoria"],
                ["modality","Modalidade"], ["fee","Taxa"], ["registration_deadline","Prazo inscrição"],
                ["registration_link","Link inscrição"],
              ].map(([k, l, req]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-slate-300 text-xs">{l}</Label>
                  <Input type={k === "date" || k === "registration_deadline" ? "date" : k === "time" ? "time" : k === "fee" ? "number" : "text"}
                    min={k === "fee" ? "0" : undefined} step={k === "fee" ? "0.01" : undefined}
                    value={form[k]} required={req || k === "fee"} onChange={(e) => set(k, e.target.value)}
                    className="bg-[#0D2A52] border-amber-500/20 text-white" />
                </div>
              ))}
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-slate-300 text-xs">Informações</Label>
                <Textarea rows={3} value={form.info} onChange={(e) => set("info", e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20 text-white" />
              </div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
                <span className="text-slate-300">Destacar torneio</span>
              </label>
              <div className="sm:col-span-2 space-y-3 border-t border-white/10 pt-4">
                <div className="flex justify-between gap-2">
                  <Label htmlFor="participant-search" className="text-slate-300">Atletas participantes</Label>
                  <span className="text-xs text-amber-300" aria-live="polite">{form.participant_ids.length} selecionado(s)</span>
                </div>
                <p className="text-xs text-slate-400">O PDF incluirá nome, categoria, escola e e-mail dos atletas selecionados.</p>
                <Input id="participant-search" placeholder="Buscar por nome, categoria, escola ou e-mail"
                  value={query} onChange={e => setQuery(e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20 text-white" />
                {loadingAthletes && <p role="status" className="text-sm text-slate-400">Carregando atletas…</p>}
                {athleteError && <div role="alert" className="text-sm text-red-300">
                  Não foi possível carregar os atletas.
                  <Button type="button" variant="outline" size="sm" onClick={loadAthletes} className="ml-2">Tentar novamente</Button>
                </div>}
                {!loadingAthletes && !athleteError && <>
                  <div className="max-h-60 overflow-y-auto rounded border border-white/10 divide-y divide-white/10">
                    {filteredAthletes.map(athlete => <label key={athlete.id} className="flex gap-3 p-3 cursor-pointer hover:bg-white/5">
                      <input type="checkbox" checked={form.participant_ids.includes(athlete.id)}
                        onChange={() => toggleAthlete(athlete.id)} className="mt-1 accent-amber-500" />
                      <span className="min-w-0">
                        <span className="text-sm text-white block">{athlete.name}{athlete.active === false ? " (inativo)" : ""}</span>
                        <span className="text-xs text-slate-400 block break-words">{[athlete.category, athlete.school, athlete.email].filter(Boolean).join(" • ")}</span>
                      </span>
                    </label>)}
                    {!filteredAthletes.length && <p className="text-sm text-slate-400 p-3">{athletes.length ? "Nenhum atleta encontrado." : "Cadastre atletas para selecionar os participantes."}</p>}
                  </div>
                  {missingParticipants.map(id => <div key={id} className="flex justify-between items-center text-sm text-amber-300 gap-2">
                    <span>Um participante não está mais disponível no cadastro.</span>
                    <Button type="button" size="sm" variant="outline" onClick={() => toggleAthlete(id)}>Remover da lista</Button>
                  </div>)}
                </>}
              </div>
              <Button type="submit" disabled={saving || loadingAthletes || athleteError || !!missingParticipants.length}
                className="sm:col-span-2 bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">{saving ? "Salvando…" : editingId ? "Salvar alterações" : "Criar"}</Button>
              </fieldset>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(t => (
          <Card key={t.id} className="bg-transparent gsx-card-dark p-5">
            <div className="text-xs text-amber-300 uppercase tracking-widest font-mono">{t.date} • {t.time}</div>
            <div className="text-white font-semibold mt-1">{t.name}</div>
            <div className="text-xs text-slate-400">{t.city} • {t.modality} • {t.fee}</div>
            <p className="text-xs text-slate-300 mt-2">{(t.participant_ids || []).length} atleta(s) participante(s)</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => begin(t)}
                className="border-amber-400/70 bg-amber-500/15 text-amber-200 hover:bg-amber-400 hover:text-[#071A33] hover:border-amber-300">
                Editar / Participantes
              </Button>
              <Button size="sm" className="bg-amber-500 text-[#071A33] hover:bg-amber-400"
                disabled={!!exporting || !(t.participant_ids || []).length} onClick={() => exportPdf(t)}>
                {exporting === t.id ? "Gerando PDF…" : "Baixar lista em PDF"}
              </Button>
            <AdminDeleteConfirmation itemName={t.name || t.title || "este torneio"} onConfirm={() => remove(t.id)}><Button size="sm" variant="destructive">Excluir</Button></AdminDeleteConfirmation>
            </div>
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}
